/**
 * What the pricebook item form still needs before it will save.
 *
 * One list for both routes. The new-item form and the edit form validate the
 * same item against the same rules, so a checklist that drifts between them
 * would be telling one of the two a lie — and the checklist is the only place
 * the form states its requirements BEFORE a save attempt. NOT-641 was exactly
 * that: equipment could not be saved without a category, the checklist listed
 * Type / Name / Code, and the save failed into a toast that faded.
 *
 * The equipment-category rule lives here too, as `equipmentCategoryRequirement`
 * — the single predicate BOTH the routes' `equipmentCategoryKey` refine and the
 * row below are derived from. Sharing the list but not the rule was how the
 * first cut of this fix still let the two disagree: the edit route lets a
 * legacy link-less item save untouched, and the row went on insisting a
 * category was missing over a Save that worked.
 */

/**
 * Where a form stands on the one field an equipment type cannot be born
 * without.
 *
 * - `not-applicable` — nothing is asking for a category. The item is not
 *   equipment, or (edit route) it is a legacy item with no linked type and
 *   empty spec panels, so this save writes no type at all. Grandfathering:
 *   repricing or archiving such an item must not be blocked on describing
 *   equipment nobody asked about.
 * - `satisfied` — a linked type already answers it, or one is chosen.
 * - `missing` — a type will be written and nothing says which category it is.
 *   The only state that blocks a save.
 */
export type EquipmentCategoryRequirement = 'not-applicable' | 'satisfied' | 'missing';

export interface EquipmentCategoryRuleInput {
  /** The item is equipment, or is being created as equipment. */
  isEquipment: boolean;
  /** An equipment type the item already links; its category is settled. */
  equipmentDescriptionId?: string | null;
  /** The category chosen on the form. */
  equipmentCategoryKey?: string | null;
  /**
   * Whether this save writes an equipment type when none is linked yet. Always
   * true on the new-item form — the spec panel there IS the type, created and
   * linked on save. On the edit form it is what the operator has actually typed
   * into the panels, because an item that predates the rule links nothing and
   * must stay saveable with them left alone.
   */
  writesEquipmentType: boolean;
}

/** The one rule; the refine and the checklist row are both read off it. */
export function equipmentCategoryRequirement({
  isEquipment,
  equipmentDescriptionId,
  equipmentCategoryKey,
  writesEquipmentType,
}: EquipmentCategoryRuleInput): EquipmentCategoryRequirement {
  if (!isEquipment) return 'not-applicable';
  // Linking an existing type answers the question — nothing is created.
  if ((equipmentDescriptionId ?? '').trim() !== '') return 'satisfied';
  if (!writesEquipmentType) return 'not-applicable';
  return (equipmentCategoryKey ?? '').trim() !== '' ? 'satisfied' : 'missing';
}

export interface PricebookChecklistItem {
  label: string;
  done: boolean;
  optional?: boolean;
}

export interface PricebookChecklistInput {
  /** Fixed after creation, so the edit form has it settled from the start. */
  typeDone: boolean;
  name: string;
  code: string;
  /** Exactly what the routes' `equipmentCategoryKey` refine is given. */
  equipmentCategory: EquipmentCategoryRuleInput;
  isMembership: boolean;
  membershipPlanId?: string;
  hasOptionalDetails: boolean;
}

export function pricebookItemChecklist({
  typeDone,
  name,
  code,
  equipmentCategory,
  isMembership,
  membershipPlanId,
  hasOptionalDetails,
}: PricebookChecklistInput): PricebookChecklistItem[] {
  const categoryRequirement = equipmentCategoryRequirement(equipmentCategory);
  return [
    { label: 'Type', done: typeDone },
    { label: 'Name', done: Boolean(name.trim()) },
    { label: 'Code', done: Boolean(code.trim()) },
    // An equipment item is saved together with the equipment type it prices,
    // and the type cannot be born without a category.
    //
    // The row is left off entirely when nothing is asking. A ticked box beside
    // "Equipment category" on a legacy item that has no category would be a
    // claim about the item's data, and there is none; silence says the honest
    // thing — this save is not about equipment specs. Type into the panels and
    // the row appears, unticked, the same moment the save starts requiring it.
    ...(categoryRequirement === 'not-applicable'
      ? []
      : [{ label: 'Equipment category', done: categoryRequirement === 'satisfied' }]),
    ...(isMembership ? [{ label: 'Membership plan', done: Boolean(membershipPlanId) }] : []),
    { label: 'Optional details', done: hasOptionalDetails, optional: true },
  ];
}
